// ============================================
// Pixel Art Post-Process Effect
// ============================================
// Virtual pixel grid + ordered Bayer dithering + palette quantization.
// Palettes: full RGB levels, grayscale, original Game Boy DMG greens.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { PIXEL_ART_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Palette mode for the Pixel Art effect
 */
export enum PixelArtPalette {
  RGB = 0,
  Grayscale = 1,
  GameBoy = 2,
}

/**
 * Pixel Art post-processing effect
 */
export class PixelArtEffect extends PostProcessEffect {
  readonly type = 'pixelArt';
  readonly displayName = 'Pixel Art';

  // Parameters
  pixelSize = 4;
  levels = 5;
  dither = 0.6;
  paletteMode = PixelArtPalette.RGB;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = PIXEL_ART_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'pixelSize', type: 'int', min: 1, max: 16, default: 4, description: 'Virtual pixel size' },
      { name: 'levels', type: 'int', min: 2, max: 16, default: 5, description: 'Color levels per channel' },
      { name: 'dither', type: 'float', min: 0, max: 1, default: 0.6, description: 'Ordered dither strength' },
      { name: 'paletteMode', type: 'int', min: 0, max: 2, default: 0, description: 'Palette (0=RGB, 1=gray, 2=Game Boy)' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'pixelSize': return this.pixelSize;
      case 'levels': return this.levels;
      case 'dither': return this.dither;
      case 'paletteMode': return this.paletteMode;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'pixelSize': this.pixelSize = Math.floor(Math.max(1, Math.min(32, value))); break;
      case 'levels': this.levels = Math.floor(Math.max(2, Math.min(32, value))); break;
      case 'dither': this.dither = Math.max(0, Math.min(1, value)); break;
      case 'paletteMode': this.paletteMode = Math.floor(Math.max(0, Math.min(2, value))) as PixelArtPalette; break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const pixelArtWgsl = getCompiledShader('postprocess/pixel-art');
    this.shader = ctx.createShader(pixelArtWgsl, pixelArtWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'pixel-art-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'pixel-art-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      pixelSize: this.pixelSize,
      levels: this.levels,
      dither: this.dither * intensity,
      paletteMode: this.paletteMode,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
